import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { ComponentProps, forwardRef, ReactNode } from 'react';

export const Sheet = ({
  modal = false,
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root modal={modal} {...props} />
);
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

type SheetSide = 'top' | 'right' | 'bottom' | 'left';
type SheetWidth = 'default' | 'wide' | 'full';

const sideClasses: Record<SheetSide, string> = {
  right:
    'tw-inset-y-0 tw-right-0 tw-h-full tw-border-l tw-border-grayscale-20 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=open]:tw-slide-in-from-right data-[state=closed]:tw-slide-out-to-right',
  left: 'tw-inset-y-0 tw-left-0 tw-h-full tw-border-r tw-border-grayscale-20 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=open]:tw-slide-in-from-left data-[state=closed]:tw-slide-out-to-left',
  top: 'tw-inset-x-0 tw-top-0 tw-w-full tw-border-b tw-border-grayscale-20 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=open]:tw-slide-in-from-top data-[state=closed]:tw-slide-out-to-top',
  bottom:
    'tw-inset-x-0 tw-bottom-0 tw-w-full tw-border-t tw-border-grayscale-20 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=open]:tw-slide-in-from-bottom data-[state=closed]:tw-slide-out-to-bottom',
};

const widthClasses: Record<SheetWidth, string> = {
  default: 'tw-w-3/4 tw-max-w-[640px]',
  wide: 'tw-w-[70%] tw-max-w-[1280px]',
  full: 'tw-w-full',
};

// A plain div because Radix renders no Overlay with modal=false, which Sheet
// needs: modal mode sets pointer-events:none on <body> and blocks nested Modals.
export const SheetOverlay = forwardRef<HTMLDivElement, ComponentProps<'div'>>(
  (props, ref) => (
    <div
      {...props}
      ref={ref}
      aria-hidden
      data-sheet-overlay
      className={clsx(
        'tw-fixed tw-inset-0 tw-z-40 tw-bg-black/50',
        props.className,
      )}
    />
  ),
);
SheetOverlay.displayName = 'SheetOverlay';

const preventOutsideClose = (event: { preventDefault: () => void }) =>
  event.preventDefault();

interface SheetContentProps
  extends ComponentProps<typeof DialogPrimitive.Content> {
  side?: SheetSide;
  width?: SheetWidth;
  showCloseButton?: boolean;
  withOverlay?: boolean;
  // Closes on the overlay's click, not Radix's pointerdown-outside: closing on
  // pointerdown lets the click fall through to whatever is underneath.
  closeOnOverlayClick?: boolean;
  children?: ReactNode;
}

/**
 * Nested modals (react-responsive-modal) and Popup confirms (sweetalert2) are not Radix
 * layers, so Radix hands every Escape to the Sheet. While one is open, Escape is its to
 * handle; closing the Sheet behind it would drop the nested form's unsaved changes.
 */
const isStackedLayerOpen = () =>
  document.querySelector('.react-responsive-modal-root, .swal2-container') !==
  null;

export const SheetContent = ({
  side = 'right',
  width = 'default',
  showCloseButton = true,
  withOverlay = true,
  closeOnOverlayClick = false,
  className,
  children,
  onInteractOutside,
  onPointerDownOutside,
  onOpenAutoFocus,
  onEscapeKeyDown,
  ...props
}: SheetContentProps) => (
  <SheetPortal>
    {withOverlay &&
      (closeOnOverlayClick ? (
        <DialogPrimitive.Close asChild>
          <SheetOverlay />
        </DialogPrimitive.Close>
      ) : (
        <SheetOverlay />
      ))}
    <DialogPrimitive.Content
      {...props}
      onOpenAutoFocus={onOpenAutoFocus}
      onEscapeKeyDown={(event) => {
        if (isStackedLayerOpen()) {
          event.preventDefault();
          return;
        }
        onEscapeKeyDown?.(event);
      }}
      onInteractOutside={
        closeOnOverlayClick ? preventOutsideClose : onInteractOutside
      }
      onPointerDownOutside={
        closeOnOverlayClick ? preventOutsideClose : onPointerDownOutside
      }
      className={clsx(
        'tw-fixed tw-z-50 tw-flex tw-flex-col tw-bg-white tw-shadow-lg tw-duration-300',
        sideClasses[side],
        side === 'right' || side === 'left' ? widthClasses[width] : '',
        className,
      )}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          aria-label="Close"
          className="tw-absolute tw-right-4 tw-top-4 tw-rounded tw-text-xl tw-leading-none tw-text-grayscale-100 hover:tw-text-grayscale-180"
          data-cy="sheet_close"
        >
          ×
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </SheetPortal>
);

export const SheetHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={clsx(
      'tw-flex tw-flex-col tw-gap-1 tw-border-b tw-border-grayscale-20 tw-p-6',
      className,
    )}
    {...props}
  />
);

export const SheetFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={clsx(
      'tw-mt-auto tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-grayscale-20 tw-p-6',
      className,
    )}
    {...props}
  />
);

export const SheetTitle = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    className={clsx(
      'tw-m-0 tw-text-lg tw-font-semibold tw-text-grayscale-180',
      className,
    )}
    {...props}
  />
);

export const SheetDescription = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={clsx('tw-text-sm tw-text-grayscale-100', className)}
    {...props}
  />
);

export const SheetBody = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={clsx('tw-flex-1 tw-overflow-y-auto tw-p-6', className)}
    {...props}
  />
);
